import type { Middleware } from '@reduxjs/toolkit';

const REDACTED = '[REDACTED]';
const SENSITIVE_KEYS = new Set([
  'accessToken',
  'authorization',
  'code',
  'confirmPassword',
  'cookie',
  'currentPassword',
  'newPassword',
  'otp',
  'password',
  'refreshToken',
  'token',
]);

function redactForLogging(value: unknown, seen = new WeakSet<object>()): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular]';

  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactForLogging(item, seen));
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEYS.has(key) ? REDACTED : redactForLogging(item, seen),
    ]),
  );
}

/**
 * Dev-only — logs every dispatched action plus the resulting state to the console (Metro
 * terminal / `adb logcat`), so the whole app's Redux activity (auth, favorites, booking draft,
 * preferences, UI, and every RTK Query request/response) is visible without attaching a separate
 * devtools client. No-op in production builds.
 */
export const loggerMiddleware: Middleware = (store) => (next) => (action) => {
  if (!(process.env.NODE_ENV !== 'production')) return next(action);

  const type = action && typeof action === 'object' && 'type' in action ? String((action as { type: unknown }).type) : String(action);
  const result = next(action);
  const nextState = store.getState();

  console.log(`[redux] ${type}`);
  if (action && typeof action === 'object' && 'payload' in action) {
    console.log('  payload:', redactForLogging((action as { payload: unknown }).payload));
  }
  console.log('  state:', redactForLogging(nextState));

  return result;
};
